import { Spacing } from '@persona-id/design-tokens';
import { CaretDownIcon, CaretUpIcon } from '@persona-id/design-tokens/icons';
import { ComponentPropsWithoutRef, forwardRef } from 'react';
import styled, { css } from 'styled-components';

import { paragraphMixins } from 'design-system/mixins/text';
import { BASE } from 'design-system/variables/spacing';

interface TableHeaderCellProps {
  size?: 'sm' | 'md';
  sortable?: boolean;
  width?: 'auto' | 'min' | 'max' | string;
  isSticky?: boolean;
}

const StyledTableHeaderCell = styled.th<TableHeaderCellProps>`
  display: table-cell;

  background-color: ${({ theme }) => theme.color.background.secondary};
  padding: ${({ size }) => (size === 'sm' ? BASE : BASE * 1.5)}px;
  text-align: left;
  white-space: nowrap;
  ${paragraphMixins.md};
  line-height: 1;
  width: ${({ width }) => {
    switch (width) {
      case 'min':
        return '1px';
      case 'max':
        return '100%';
      default:
        if (width) {
          return width;
        } else {
          return 'auto';
        }
    }
  }};

  &:first-child {
    border-radius: ${({ theme }) =>
      `${theme.element.borderRadius} 0 0 ${theme.element.borderRadius}`};
  }

  &:last-child {
    border-radius: ${({ theme }) =>
      `0 ${theme.element.borderRadius} ${theme.element.borderRadius} 0`};
  }

  ${({ sortable }) =>
    sortable &&
    css`
      cursor: pointer;

      &:hover ${TableIconContainer} {
        display: inline;
        visibility: visible;
      }
    `};

  ${({ isSticky }) =>
    isSticky
      ? css`
          position: sticky;
          top: 0;
          z-index: 1;
        `
      : css`
          position: relative;
        `}
`;

const VerticalAlign = styled.div`
  display: flex;
  align-items: center;
`;

interface TableIconContainerProps {
  sortDirection?: 'asc' | 'desc' | false;
}

const TableIconContainer = styled('div')<TableIconContainerProps>`
  display: inline-block;
  margin-left: ${Spacing[1]};

  ${({ sortDirection }) => {
    if (sortDirection) {
      return css`
        display: inline;
      `;
    } else {
      return css`
        display: inline;
        visibility: hidden;
        color: ${({ theme }) => theme.color.text.disabled};
      `;
    }
  }};
`;

const TableHeaderCell = forwardRef<
  HTMLTableCellElement,
  ComponentPropsWithoutRef<'th'> & TableHeaderCellProps & TableIconContainerProps
>(function TableHeaderCell({ sortable, sortDirection, children, ...elemProps }, ref) {
  let ariaSort: 'none' | 'ascending' | 'descending' | 'other' = 'none';

  if (sortable && sortDirection) {
    ariaSort = sortDirection === 'asc' ? 'ascending' : 'descending';
  }

  return (
    <StyledTableHeaderCell
      sortable={sortable}
      aria-sort={ariaSort}
      role='columnheader'
      {...elemProps}
      ref={ref}
    >
      <VerticalAlign>
        {children}
        {sortable && (
          <TableIconContainer sortDirection={sortDirection}>
            {sortDirection === 'asc' ? <CaretUpIcon /> : <CaretDownIcon />}
          </TableIconContainer>
        )}
      </VerticalAlign>
    </StyledTableHeaderCell>
  );
});

export default TableHeaderCell;
